import { ReactNode, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  CustomSegmented,
  CustomSegmentedProps,
  NavigateAwayModal,
} from '../../components';
import {
  ContentSegmentOption,
  ContentSegmentsProvider,
  useCurrentSegment,
} from './ContentSegmentsContext';
import { Flex } from 'antd';

export interface ContentSegmentsProps<Segment extends string = string>
  extends Omit<CustomSegmentedProps, 'value' | 'options'> {
  segments: ContentSegmentOption<Segment>[];
  onChangeSegment?: (value: Segment) => void;
  shouldBlock?: () => boolean;
  onSupressBlock?: () => void;
}

export const ContentSegments = <Segment extends string = string>(
  props: ContentSegmentsProps<Segment>
) => (
  <ContentSegmentsProvider
    segments={props.segments}
    onChangeSegment={props.onChangeSegment}
  >
    <ContentSegmentsInner<Segment> {...props} />
  </ContentSegmentsProvider>
);

const ContentSegmentsInner = <Segment extends string = string>(
  props: ContentSegmentsProps<Segment>
) => {
  const {
    segments,
    onChangeSegment,
    children,
    shouldBlock,
    onSupressBlock,
    ...rest
  } = props;
  const { currentSegment, setCurrentSegment } = useCurrentSegment<Segment>();
  const [nextSegment, setNextSegment] = useState<Segment | undefined>(
    undefined
  );
  const onChange = useCallback(
    (segment: Segment) => {
      if (shouldBlock?.()) {
        setNextSegment(segment);
        return;
      }

      setCurrentSegment(segment);
      onChangeSegment?.(segment);
    },
    [onChangeSegment, setCurrentSegment, shouldBlock]
  );

  const handleNavigateAwayOk = useCallback(() => {
    if (!nextSegment) return;

    onSupressBlock?.();
    setCurrentSegment(nextSegment);
    onChangeSegment?.(nextSegment);
    setNextSegment(undefined);
  }, [nextSegment, onChangeSegment, onSupressBlock, setCurrentSegment]);

  const handleNavigateAwayCancel = useCallback(() => {
    setNextSegment(undefined);
  }, []);

  const { options, childrenMap } = useMemo(() => {
    const childrenMap: { [key: string]: ReactNode } = {};
    const options = segments.map(({ children, value, ...rest }) => {
      childrenMap[value] = children;
      return { value, ...rest };
    });
    return { options, childrenMap };
  }, [segments]);

  return (
    <Flex vertical gap="large" align="center">
      <StyledCustomSegmented<Segment>
        options={options}
        value={currentSegment}
        onChange={onChange}
        {...rest}
      />
      {childrenMap[currentSegment]}
      {children}
      {nextSegment && (
        <NavigateAwayModal
          onOk={handleNavigateAwayOk}
          onCancel={handleNavigateAwayCancel}
        />
      )}
    </Flex>
  );
};

const StyledCustomSegmented: typeof CustomSegmented = styled(CustomSegmented)`
  position: sticky;
  left: 50%;
  transform: translateX(-50%);
  margin-inline: -999999px;

  .ant-segmented-item-label {
    position: relative;
    border-radius: inherit;
  }
`;
